import React, { useCallback, useEffect, useState } from "react";
import { Button, Flex } from "@chakra-ui/react";
import { Get, Post } from "api";
import { NAVBAR_HEIGHT } from "components/navbar/NavbarAdmin";
import { useHistory, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import RepeatableSetupForm from "views/admin/initialSetup/components/RepeatableSetupForm";
import SortableSetupForm from "views/admin/initialSetup/components/SortableSetupForm";
import { useSearchParams } from "hooks/useSearchParams";

type GameChallengesRouteParams = {
  appId: string;
  partnerId: string;
};

type AppEvent = {
  eventName: string;
  eventTitle: string;
};

type SortableSetup = {
  appEvents: AppEvent[];
};

type RoasGoal = {
  dx: number;
  roasGoal: number;
};

export type SortableConfig = {
  partnerId: number;
  platformId: number;
  appId: string;
  sortableSetup: SortableSetup;
  roasGoals: RoasGoal[];
  roasIapShare: number;
  revenueEventName: string;
  isRevenueValueShared: boolean;
  isSubmission: boolean;
};

type RepeatableSetup = {
  repeatableEventName: string;
  repeatableUnit: string;
  repeatableFinishedByAM: number;
};

export type RepeatableConfig = {
  partnerId: number;
  platformId: number;
  appId: string;
  repeatableSetup: RepeatableSetup;
  roasGoals: RoasGoal[];
  roasIapShare: number;
  revenueEventName: string;
  isRevenueValueShared: boolean;
  isSubmission: boolean;
};

type EventType = "repeatable" | "sortable";

const EVENT_TYPE_BUTTONS = [
  {
    label: "Recurring Event",
    value: "repeatable",
  },
  {
    label: "List of Events",
    value: "sortable",
  },
];

const InitialSetup = () => {
  document.title = "Gamelight - Game Configuration";
  const history = useHistory();
  const searchParams = useSearchParams();
  const params = useParams<GameChallengesRouteParams>();
  const [appLoading, setAppLoading] = useState<boolean>(true);
  const [setupLoading, setSetupLoading] = useState<boolean>(true);
  const [eventType, setEventType] = useState<EventType>(
    (searchParams.get("type") as EventType) ?? "repeatable"
  );
  const [appDetails, setAppDetails] = useState<any>();
  const [repeatableSetup, setRepeatableSetup] = useState<RepeatableConfig[]>(
    []
  );
  const [sortableSetup, setSortableSetup] = useState<SortableConfig[]>([]);

  const fetchAppDetails = useCallback(async () => {
    setAppLoading(true);
    try {
      const response = await Post("/manage/partner/app", {
        partnerId: Number(params.partnerId),
        appId: params.appId,
      });
      setAppDetails(response);
    } catch (error) {
      console.warn(error);
    } finally {
      setAppLoading(false);
    }
  }, [params.appId, params.partnerId]);

  const fetchSetups = useCallback(async () => {
    setSetupLoading(true);

    try {
      const repeatableSetup = Get(
        `/manage/partner/${params.partnerId}/app/${params.appId}/setup/repeatable`
      );
      const sortableSetup = Get(
        `/manage/partner/${params.partnerId}/app/${params.appId}/setup/sortable`
      );

      const responses = await Promise.all([repeatableSetup, sortableSetup]);

      setRepeatableSetup((responses[0] as any[]).reverse());
      setSortableSetup((responses[1] as any[]).reverse());
    } catch (error) {
      console.error("setup loading error: ", error);
    } finally {
      setSetupLoading(false);
    }
  }, [params.appId, params.partnerId]);

  useEffect(() => {
    fetchAppDetails();
    fetchSetups();
  }, [fetchAppDetails, fetchSetups]);

  return (
    <Flex
      flex={1}
      pt={NAVBAR_HEIGHT}
      minH={"90vh"}
      flexDirection="column"
      gap={8}
    >
      <Flex alignItems={"center"} gap={8} justifyContent="center">
        {EVENT_TYPE_BUTTONS.map((button) => (
          <Button
            colorScheme={"brand"}
            variant="ghost"
            opacity={eventType === button.value ? 1 : 0.8}
            color={eventType === button.value ? "brand.500" : "gray.500"}
            _hover={{
              bgColor: eventType === button.value ? "white" : "gray.50",
            }}
            size={"lg"}
            flex={1}
            bgColor={eventType === button.value ? "white" : "transparent"}
            onClick={() => {
              setEventType(button.value as EventType);
              history.push({
                search: `?type=${button.value}`,
              });
            }}
            py={8}
            key={button.value}
            as={motion.button}
            whileHover={{ scale: 1.05, opacity: 1 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: eventType === button.value ? 1 : 0.8 }}
            isDisabled={appLoading || setupLoading}
          >
            {button.label}
          </Button>
        ))}
      </Flex>
      {appLoading || setupLoading ? (
        <></>
      ) : eventType === "repeatable" ? (
        <RepeatableSetupForm
          platformId={appDetails?.appPlatform === "Android" ? 1 : 2}
          initialData={repeatableSetup}
        />
      ) : (
        <SortableSetupForm
          platformId={appDetails?.appPlatform === "Android" ? 1 : 2}
          initialData={sortableSetup}
        />
      )}
    </Flex>
  );
};

export default InitialSetup;
